import React, { useState, useEffect, useCallback } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import Layout from '../components/Layout';
import { amcAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const fmtNum = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtDate = d => formatDateDMY(d, '—');

const AMC = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#E2E8F0' : '#1E293B' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#FFFFFF', borderRadius: 12, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, padding: 20 };
  const inp = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#E2E8F0' : '#1E293B', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const btnPrimary = { padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const btnSecondary = { padding: '8px 18px', background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#CBD5E1' : '#64748B', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
  const lbl = { ...muted, fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block' };

  const [tab, setTab] = useState('all');
  const [contracts, setContracts] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({ customer_id: '', customer_name: '', product_description: '', start_date: new Date().toISOString().slice(0, 10), end_date: '', amount: 0, billing_cycle: 'yearly', auto_renew: true });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, eRes, sRes] = await Promise.all([
        amcAPI.list(tab === 'expiring' ? {} : { status: tab === 'all' ? undefined : tab }),
        amcAPI.expiring({ days: 30 }).catch(() => ({ data: [] })),
        amcAPI.stats().catch(() => ({ data: {} })),
      ]);
      setContracts(Array.isArray(cRes.data) ? cRes.data : []);
      setExpiring(Array.isArray(eRes.data) ? eRes.data : []);
      setStats(sRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tab]);

  useEffect(() => { loadData(); }, [loadData]);

  const createContract = async (e) => {
    e.preventDefault();
    try {
      await amcAPI.create({
        customer_id: form.customer_id || null,
        customer_name: form.customer_name,
        product_description: form.product_description,
        start_date: form.start_date,
        end_date: form.end_date,
        billing_cycle: form.billing_cycle,
        amount: Number(form.amount || 0),
        auto_renew: !!form.auto_renew,
      });
      setShowForm(false);
      setForm({ customer_id: '', customer_name: '', product_description: '', start_date: new Date().toISOString().slice(0, 10), end_date: '', amount: 0, billing_cycle: 'yearly', auto_renew: true });
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const renewContract = async (id) => {
    try { await amcAPI.renew(id); loadData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const cancelContract = async (id) => {
    if (!window.confirm('Cancel this contract?')) return;
    try { await amcAPI.cancel(id); loadData(); } catch (e) { alert('Failed'); }
  };

  const statusColors = { active: '#10B981', expired: '#EF4444', cancelled: '#6B7280', pending_renewal: '#F59E0B' };

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ ...pg, fontSize: 22, fontWeight: 800 }}>📅 AMC Contracts</h1>
          <button onClick={() => setShowForm(!showForm)} style={btnPrimary}>{showForm ? 'Cancel' : '+ New Contract'}</button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="stagger-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div style={{ ...card, borderTop: `4px solid #10B981` }}><div style={muted}>Active</div><div style={{ ...pg, fontSize: 24, fontWeight: 800 }}>{stats.active || 0}</div></div>
            <div style={{ ...card, borderTop: `4px solid #F59E0B` }}><div style={muted}>Expiring (30d)</div><div style={{ ...pg, fontSize: 24, fontWeight: 800, color: '#F59E0B' }}>{stats.expiring_soon || expiring.length}</div></div>
            <div style={{ ...card, borderTop: `4px solid #EF4444` }}><div style={muted}>Expired</div><div style={{ ...pg, fontSize: 24, fontWeight: 800 }}>{stats.expired || 0}</div></div>
            <div style={{ ...card, borderTop: `4px solid ${acc}` }}><div style={muted}>Active Contract Value</div><div style={{ ...pg, fontSize: 20, fontWeight: 800 }}>₹{fmtNum(stats.active_value)}</div></div>
          </div>
        )}

        {/* Expiring Alert */}
        {expiring.length > 0 && (
          <div style={{ ...card, marginBottom: 16, borderLeft: `4px solid #F59E0B`, background: darkMode ? '#1C1917' : '#FFFBEB' }}>
            <div style={{ fontWeight: 700, color: '#F59E0B', marginBottom: 8 }}>⚠️ {expiring.length} contracts expiring within 30 days</div>
            {expiring.slice(0, 3).map(c => (
              <div key={c.id} style={{ fontSize: 13, ...pg, display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>{c.contract_number} — {c.customer_name}</span>
                <span style={muted}>Expires: {fmtDate(c.end_date)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['all', 'active', 'expired', 'cancelled'].map(f => (
            <button key={f} onClick={() => setTab(f)}
              style={{ padding: '6px 16px', borderRadius: 8, border: `1px solid ${tab === f ? acc : (darkMode ? '#334155' : '#E2E8F0')}`, background: tab === f ? acc : 'transparent', color: tab === f ? '#fff' : pg.color, fontWeight: 600, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize' }}>
              {f}
            </button>
          ))}
        </div>

        {/* Create Form */}
        {showForm && (
          <form onSubmit={createContract} style={{ ...card, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
              <div><label style={lbl}>Customer ID</label><input value={form.customer_id} onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Customer Name *</label><input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} style={inp} required /></div>
              <div><label style={lbl}>Product / Service</label><input value={form.product_description} onChange={e => setForm(f => ({ ...f, product_description: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Start Date</label><StrictDateInput value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inp} required /></div>
              <div><label style={lbl}>End Date</label><StrictDateInput value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={inp} required /></div>
              <div><label style={lbl}>Amount (₹)</label><input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))} style={inp} required /></div>
              <div><label style={lbl}>Billing Cycle</label>
                <select value={form.billing_cycle} onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))} style={inp}>
                  {['monthly', 'quarterly', 'half_yearly', 'yearly'].map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                <input type="checkbox" checked={form.auto_renew} onChange={e => setForm(f => ({ ...f, auto_renew: e.target.checked }))} />
                <label style={{ ...pg, fontSize: 13 }}>Auto Renew</label>
              </div>
            </div>
            <button type="submit" style={btnPrimary}>Create Contract</button>
          </form>
        )}

        {/* List */}
        {loading ? <div style={{ ...muted, textAlign: 'center', padding: 40 }}>Loading...</div> : contracts.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No AMC contracts found</div>
        ) : (
          <div style={card}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                  {['Contract #', 'Customer', 'Start', 'End', 'Amount', 'Cycle', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contracts.map(c => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                    <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>{c.contract_number}</td>
                    <td style={{ padding: '8px 10px', ...pg }}>{c.customer_name}</td>
                    <td style={{ padding: '8px 10px', ...pg }}>{fmtDate(c.start_date)}</td>
                    <td style={{ padding: '8px 10px', ...pg }}>{fmtDate(c.end_date)}</td>
                    <td style={{ padding: '8px 10px', ...pg, fontWeight: 700 }}>₹{fmtNum(c.amount)}</td>
                    <td style={{ padding: '8px 10px', ...pg, textTransform: 'capitalize' }}>{c.billing_cycle?.replace(/_/g, ' ')}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: `${statusColors[c.status] || acc}22`, color: statusColors[c.status] || acc }}>
                        {c.status?.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', display: 'flex', gap: 4 }}>
                      {(c.status === 'active' || c.status === 'expired') && (
                        <button onClick={() => renewContract(c.id)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11 }}>Renew</button>
                      )}
                      {c.status === 'active' && (
                        <button onClick={() => cancelContract(c.id)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11, color: '#EF4444' }}>Cancel</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AMC;
